import { FAST_URL } from '@/environment'
import { queryParams } from '@attensi/utils'
import { getAccessToken } from '../activeSession'
import { FastApiV1Error } from './FastApiV1Error'

type QueryValue = string | number | boolean

type FastApiV1FetchOptions = {
  query?: Record<string, QueryValue>
}

/**
 * Cross-app GET against hyperdrive `api/v1` (not the v3 admin API). Forwards
 * Manager's existing OAuth token as `Authorization: Bearer <jwt>` — the token
 * is already shape-compatible with hyperdrive's contract (HS512,
 * `iss: "Attensi-backend"`, top-level `user_id`/`roles`/`company.id`), no mint
 * or caching layer needed. Normalizes non-2xx into `FastApiV1Error` carrying
 * `.status` so consumers can pattern-match on 403 / other statuses.
 *
 * Unlike `customFetch`, a 401 does not redirect to login: Manager's own requests
 * already catch an expired token, so a 401 here means Fast rejects a token
 * Manager accepts, and logging in again returns the same token.
 *
 * GET-only until a real write caller lands — extend then, together with 204
 * handling that a POST/PATCH/DELETE would need.
 */
export const fastApiV1Fetch = async <T>(
  path: string,
  { query }: FastApiV1FetchOptions = {}
): Promise<T> => {
  const jwt = await getAccessToken()

  // FAST_URL includes the player path (/users/attensi) for `{FAST_URL}/play`;
  // the hyperdrive api/v1 endpoints live at the domain root, so use the origin.
  const base = `${new URL(FAST_URL).origin}/api/v1${path}`
  const url = query ? queryParams(base, query) : base

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${jwt}` },
  })

  if (!response.ok) {
    throw new FastApiV1Error(response.status, response.statusText)
  }

  return (await response.json()) as T
}
